import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object | object[];
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema,
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }
    if (canonical) {
      updateMetaTag('og:url', canonical, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Update canonical link
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', canonical);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Remove existing Schema.org JSON-LD scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((script) => script.remove());

    // Add Schema.org JSON-LD
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((schemaObj) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(schemaObj);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, canonical, ogImage, schema]);
}

// Schema.org generators
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'Brushour Painters & Decorators',
  description: 'Professional painters and decorators in Watford, Hertfordshire with 18+ years of experience. Interior painting, exterior painting, wallpaper installation, and commercial painting.',
  url: SITE_URL,
  telephone: '+447595037441',
  email: 'daniel@brushour.co.uk',
  image: 'https://static.readdy.ai/image/3d769dc1e01059223eb26668720a1761/e6c157647967ffb5fa62a9e4cb4fe039.png',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '111 Redwood Close',
    addressLocality: 'Watford',
    addressRegion: 'Hertfordshire',
    postalCode: 'WD19 6ST',
    addressCountry: 'GB',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 51.6565,
    longitude: -0.3903,
  },
  areaServed: [
    { '@type': 'City', name: 'Watford' },
    { '@type': 'City', name: 'Bushey' },
    { '@type': 'City', name: 'Pinner' },
    { '@type': 'City', name: 'Radlett' },
    { '@type': 'City', name: 'Rickmansworth' },
    { '@type': 'City', name: 'Northwood' },
    { '@type': 'City', name: 'Chorleywood' },
    { '@type': 'City', name: 'Gerrards Cross' },
  ],
  priceRange: '££',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '09:00',
      closes: '17:00',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '47',
  },
  sameAs: [
    'https://www.facebook.com/brushour',
    'https://www.instagram.com/brushour_',
    'https://www.tiktok.com/@brushour_',
    'https://www.youtube.com/@brushour',
    'https://maps.app.goo.gl/aKHCQ3m9gctrcMQ76',
    'https://www.google.com/maps/place/?q=place_id:ChIJBa--2zlrdkgRYGtcER3iX4g',
  ],
});

export const generateWebPageSchema = (pageName: string, description: string, url?: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: pageName,
  description: description,
  url: url || SITE_URL,
  publisher: {
    '@type': 'Organization',
    name: 'Brushour',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/3d769dc1e01059223eb26668720a1761/e6c157647967ffb5fa62a9e4cb4fe039.png',
    },
  },
});

export const generateServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Painting and Decorating',
  provider: {
    '@type': 'ProfessionalService',
    name: 'Brushour',
    telephone: '+447595037441',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '111 Redwood Close',
      addressLocality: 'Watford',
      addressRegion: 'Hertfordshire',
      postalCode: 'WD19 6ST',
      addressCountry: 'GB',
    },
  },
  areaServed: {
    '@type': 'City',
    name: 'Watford',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Painting and Decorating Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Interior Painting',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Exterior Painting',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Wallpaper Installation',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Commercial Painting',
        },
      },
    ],
  },
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateImageGallerySchema = (images: Array<{ url: string; caption: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  name: 'Brushour Project Gallery',
  description: 'Gallery of completed painting and decorating projects',
  image: images.map((img) => ({
    '@type': 'ImageObject',
    url: img.url,
    caption: img.caption,
  })),
});

export const generateBlogPostSchema = (
  title: string,
  description: string,
  datePublished: string,
  image?: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: title,
  description: description,
  datePublished: datePublished,
  author: {
    '@type': 'Organization',
    name: 'Brushour',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Brushour',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/3d769dc1e01059223eb26668720a1761/e6c157647967ffb5fa62a9e4cb4fe039.png',
    },
  },
  ...(image && { image: image }),
});

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const generateContactPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Brushour',
  description: 'Get in touch with Brushour for professional painting and decorating services in Watford',
  url: `${SITE_URL}/contact`,
  mainEntity: {
    '@type': 'ProfessionalService',
    name: 'Brushour',
    telephone: '+447595037441',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '111 Redwood Close',
      addressLocality: 'Watford',
      addressRegion: 'Hertfordshire',
      postalCode: 'WD19 6ST',
      addressCountry: 'GB',
    },
  },
});

export const generateAboutPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Brushour',
  description: 'Learn about Brushour, professional painters and decorators serving Watford and Hertfordshire for over 18 years',
  url: `${SITE_URL}/about`,
  mainEntity: {
    '@type': 'ProfessionalService',
    name: 'Brushour',
    description: 'Professional painters and decorators in Watford, Hertfordshire with 18+ years of experience',
    foundingDate: '2006',
  },
});

export const generateGallerySchema = (images: Array<{ url: string; caption: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  name: 'Brushour Project Gallery – Painting & Decorating Portfolio',
  description: 'Gallery of completed painting and decorating projects across Watford, Hertfordshire and surrounding areas by Brushour.',
  url: `${SITE_URL}/gallery`,
  image: images.map((img) => ({
    '@type': 'ImageObject',
    url: img.url,
    caption: img.caption,
  })),
});

export const generateTestimonialsSchema = (reviews: Array<{ author: string; rating: number; text: string; date: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Brushour Painters & Decorators',
  url: `${SITE_URL}/testimonials`,
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: String(reviews.length || 47),
    bestRating: '5',
    worstRating: '1',
  },
  review: reviews.map((r) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.author },
    reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5' },
    reviewBody: r.text,
    datePublished: r.date,
  })),
});

export const generateBookingSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Book a Free Painting & Decorating Consultation – Brushour',
  description: 'Schedule a free, no-obligation consultation and quote for painting and decorating services in Watford and Hertfordshire.',
  url: `${SITE_URL}/booking`,
  provider: {
    '@type': 'ProfessionalService',
    name: 'Brushour',
    telephone: '+447595037441',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '111 Redwood Close',
      addressLocality: 'Watford',
      addressRegion: 'Hertfordshire',
      postalCode: 'WD19 6ST',
      addressCountry: 'GB',
    },
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'GBP',
    description: 'Free, no-obligation quote and consultation',
  },
});

export const generateAreaPageSchema = (
  areaName: string,
  region: string,
  slug: string,
  latitude: number,
  longitude: number,
  postalCode: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: `Brushour – Painters & Decorators ${areaName}`,
  description: `Professional painting and decorating services in ${areaName}, ${region}. Interior painting, exterior painting, wallpaper installation and more. Free quotes available.`,
  url: `${SITE_URL}/areas/${slug}`,
  telephone: '+447595037441',
  email: 'daniel@brushour.co.uk',
  address: {
    '@type': 'PostalAddress',
    addressLocality: areaName,
    addressRegion: region,
    postalCode,
    addressCountry: 'GB',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude,
    longitude,
  },
  priceRange: '££',
  areaServed: { '@type': 'City', name: areaName },
  parentOrganization: {
    '@type': 'ProfessionalService',
    name: 'Brushour',
    url: SITE_URL,
  },
});
